import { Menu, LogOut, Sun, Moon } from 'lucide-react'
import { useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import { useDarkMode } from '@/hooks/useDarkMode'
import { NotificationBadge } from '@/components/notifications/NotificationBadge'
import { NotificationDropdown } from '@/components/notifications/NotificationDropdown'
import { NotificationSoundToggle } from '@/components/notifications/NotificationSoundToggle'
import { VersionBadge } from '@/components/layout/VersionBadge'
import { personName } from '@repo/types'

interface TopBarProps {
  onMenuClick: () => void
}

export function TopBar({ onMenuClick }: TopBarProps) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const { isDark, toggle } = useDarkMode()
  const [bellOpen, setBellOpen] = useState(false)

  async function handleLogout() {
    await logout()
    navigate('/login', { replace: true })
  }

  return (
    <header className="flex items-center justify-between h-14 px-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
      <button
        onClick={onMenuClick}
        className="lg:hidden p-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
      >
        <Menu size={20} />
      </button>

      <div className="flex-1" />

      <div className="flex items-center gap-1">
        <VersionBadge />

        <button
          onClick={toggle}
          className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          title={isDark ? 'Svetlý režim' : 'Tmavý režim'}
        >
          {isDark ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        <NotificationSoundToggle />

        <div className="relative">
          <NotificationBadge onClick={() => setBellOpen((v) => !v)} />
          {bellOpen && <NotificationDropdown onClose={() => setBellOpen(false)} />}
        </div>

        {user && (
          <button
            onClick={() => navigate('/profile')}
            className="flex items-center gap-2 ml-2 rounded-lg px-1.5 py-1 -my-1 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            {user.avatarUrl ? (
              <img src={user.avatarUrl} alt={personName(user)} className="w-8 h-8 rounded-full object-cover" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-brand-600 flex items-center justify-center text-xs font-bold text-white">
                {user.firstName[0]}{user.lastName[0]}
              </div>
            )}
            <span className="hidden sm:block text-sm font-medium text-gray-700 dark:text-gray-200">
              {personName(user)}
            </span>
          </button>
        )}

        <button
          onClick={handleLogout}
          className="p-2 text-gray-500 dark:text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg ml-1"
          title="Odhlásiť sa"
        >
          <LogOut size={18} />
        </button>
      </div>
    </header>
  )
}
